/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  Box,
  IconButton,
  makeStyles,
  TextField,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { CloseIcon } from 'assets/icons/CloseIcon';
import { DeleteButton, SecondaryButton } from './Button/ReusableButton';

const useStyles = makeStyles((theme) => ({
  root: {
    textTransform: 'capitalize',
    lineHeight: 1.5,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(0),
    color: theme.palette.grey[500],
    '&:hover': {
      background: 'transparent !important',
    },
    '&:disabled': {
      backgroundColor: '#9E9E9E',
    },
  },
  containedSecondary: {
    backgroundColor: '#e13939',
    '&:hover': {
      backgroundColor: '#d41c1c ',
    },
  },
  dialogActions: {
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
  },
  hr: {
    margin: '0 0',
    border: 'none',
    borderTop: '1px solid #ccc',
  },
}));

interface ConfirmationModalProps {
  openModal?: boolean;
  setDeleteModal?: (value: boolean) => void;
  handleDelete?: () => void;
  deleteButtonLabel?: string;
  cancelButtonLabel?: string;
  buttonLoading?: boolean;
  confirmationTextDescription?: string;
  isDeleteConfirm?: boolean;
  isFirstButtonHidden?: boolean;
  isSecondButtonHidden?: boolean;
  isCloseHidden?: boolean;
  title?: React.ReactNode;
  isDivider?: boolean;
  content?: React.ReactNode;
  firstButton?: React.ReactNode;
  secondButton?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  handleCancel?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

/**
 * @deprecated Use the ConfirmationModal component from the components/molecules/Dialog package instead.
 */
export default function ConfirmationModal({
  openModal = false,
  setDeleteModal = () => {},
  handleDelete = () => {},
  deleteButtonLabel,
  cancelButtonLabel,
  buttonLoading,
  confirmationTextDescription,
  isDeleteConfirm,
  isFirstButtonHidden,
  isSecondButtonHidden,
  isCloseHidden,
  title,
  isDivider,
  content,
  firstButton,
  secondButton,
  maxWidth = 'xs',
  handleCancel,
  icon,
  className,
}: ConfirmationModalProps) {
  const [text, setText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const classes = useStyles();

  return (
    <Dialog
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={className}
      open={openModal}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {title}
        {!isCloseHidden && (
          <IconButton
            aria-label="close"
            onClick={() => {
              setDeleteModal(false);
              typeof handleCancel === 'function' && handleCancel();
            }}
            className={classes.closeButton}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      {isDivider && <hr className={classes.hr} />}
      <DialogContent>
        <Box display="flex" alignItems="center">
          <div style={{ width: 50, marginRight: 20 }}>
            {icon || (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="50"
                width="50"
                viewBox="0 0 24 24"
                fill="#E13939"
              >
                <path d="M4.47 21h15.06c1.54 0 2.5-1.67 1.73-3L13.73 4.99c-.77-1.33-2.69-1.33-3.46 0L2.74 18c-.77 1.33.19 3 1.73 3zM12 14c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1s1 .45 1 1v2c0 .55-.45 1-1 1zm1 4h-2v-2h2v2z" />
              </svg>
            )}
          </div>
          <DialogContentText
            id="alert-dialog-description"
            component="div"
            paragraph={false}
            style={{ paddingTop: '10px' }}
          >
            {content}
          </DialogContentText>
        </Box>
        {isDeleteConfirm && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              style={{
                color: ' #F44336',
                marginBottom: '0.5rem',
              }}
            >
              {confirmationTextDescription}
            </Typography>
            <TextField
              style={{
                // height: '3rem',
                background: '#F5F5F5',
                borderRadius: '4px',
                border: error ? '1px solid red' : '',
                width: 'fit-content',
              }}
              variant="outlined"
              type="text"
              value={text}
              onChange={({ target: { value } }) => {
                setError('');
                setText(value);
              }}
              autoFocus
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions classes={{ root: classes.dialogActions }}>
        {firstButton ||
          (!isFirstButtonHidden && (
            <SecondaryButton
              label={cancelButtonLabel || 'Cancel'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setDeleteModal(false);
                typeof handleCancel === 'function' && handleCancel();
              }}
              variant="outlined"
              size="small"
            />
          ))}

        {secondButton ||
          (!isSecondButtonHidden && (
            <DeleteButton
              loading={buttonLoading}
              label={deleteButtonLabel || 'Delete'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if ((!text || text !== 'DELETE') && isDeleteConfirm) {
                  setError('Field is required.');
                  return;
                }
                handleDelete();
              }}
              startIcon={buttonLoading && <CircularProgress size={20} />}
            />
          ))}
      </DialogActions>
    </Dialog>
  );
}
